import React, { useMemo, useState } from 'react';

const formatAgo = (value) => {
  const timestamp = Number(value || 0);
  if (!timestamp) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds} сек`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин`;
  return `${Math.floor(seconds / 3600)} ч`;
};

const filterGroupBySource = (group, source) => {
  if (source === 'all') return group;
  const connections = (group.connections || []).filter((item) => item.app === source);
  if (!connections.length) return null;
  return {
    ...group,
    connections,
    connectionCount: connections.length,
    clientCount: new Set(connections.map((item) => item.clientId || item.socketId)).size,
    apps: [source],
    routes: Array.from(new Set(connections.map((item) => item.route).filter(Boolean))),
    lastActivityAt: Math.max(...connections.map((item) => Number(item.lastActivityAt || 0)), 0),
  };
};

const ConnectionRow = ({ connection, onDisconnectConnection, busy }) => (
  <div className="systemMonitor__connection">
    <div className="systemMonitor__connectionMain">
      <div className="systemMonitor__connectionTop">
        <span className={`systemMonitor__appBadge is-${connection.app || 'front'}`}>{connection.app === 'admin' ? 'ADMIN' : 'FRONT'}</span>
        <strong>{connection.deviceLabel || 'Устройство'} · {connection.browserLabel || 'Браузер'}</strong>
        <span className={connection.visibility === 'hidden' ? 'is-muted' : 'is-online'}>{connection.visibility === 'hidden' ? 'фон' : 'активная вкладка'}</span>
      </div>
      <div className="systemMonitor__connectionMeta">
        <span>{connection.route || 'маршрут не передан'}</span>
        <span>{connection.transport || '—'}</span>
        {connection.ip ? <span>IP {connection.ip}</span> : null}
        <span>активность {formatAgo(connection.lastActivityAt)} назад</span>
      </div>
    </div>
    <button type="button" className="systemMonitor__miniBtn" disabled={busy} onClick={() => onDisconnectConnection(connection)}>Отключить</button>
  </div>
);

const UserCard = ({ user, currentUserId, onDisconnectUser, onDisconnectConnection, onBlock, onUnblock, onHistory, busyKey }) => {
  const [expanded, setExpanded] = useState(false);
  const isSelf = String(user.userId || '') === String(currentUserId || '');
  const protectedSuperAdmin = user.role === 'superAdmin';
  const busy = busyKey === user.userId || busyKey === `disconnect:${user.userId}`;

  return (
    <article className={`systemMonitor__userCard${user.isActive === false ? ' is-blocked' : ''}`}>
      <div className="systemMonitor__userHead">
        <div className="systemMonitor__userIdentity">
          <div className="systemMonitor__avatar">{String(user.name || 'U').charAt(0).toUpperCase()}</div>
          <div>
            <div className="systemMonitor__userNameRow">
              <strong>{user.name || 'Пользователь'}</strong>
              <span className="systemMonitor__roleBadge">{user.role || 'user'}</span>
              {user.isActive === false ? <span className="systemMonitor__blockedBadge">ЗАБЛОКИРОВАН</span> : <span className="systemMonitor__onlineBadge">ONLINE</span>}
            </div>
            <div className="systemMonitor__userSub">{user.email || user.userId || '—'}</div>
          </div>
        </div>
        <div className="systemMonitor__userStats">
          <span><b>{user.connectionCount || 0}</b> sockets</span>
          <span><b>{user.clientCount || 0}</b> clients</span>
          <span>активность {formatAgo(user.lastActivityAt)} назад</span>
        </div>
      </div>

      <div className="systemMonitor__userActions">
        <button type="button" className="systemMonitor__miniBtn" onClick={() => onHistory(user)}>История</button>
        <button type="button" className="systemMonitor__miniBtn" onClick={() => setExpanded((value) => !value)}>{expanded ? 'Скрыть соединения' : 'Соединения'}</button>
        {!isSelf ? <button type="button" className="systemMonitor__miniBtn" disabled={busy} onClick={() => onDisconnectUser(user)}>Отключить все</button> : null}
        {!isSelf && !protectedSuperAdmin && user.isActive !== false ? (
          <button type="button" className="systemMonitor__miniBtn is-danger" disabled={busy} onClick={() => onBlock(user)}>Заблокировать</button>
        ) : null}
        {!isSelf && user.isActive === false ? (
          <button type="button" className="systemMonitor__miniBtn is-success" disabled={busy} onClick={() => onUnblock(user)}>Разблокировать</button>
        ) : null}
      </div>

      {expanded ? <div className="systemMonitor__connections">{(user.connections || []).map((connection) => (
        <ConnectionRow key={connection.socketId} connection={connection} onDisconnectConnection={onDisconnectConnection} busy={busyKey === connection.socketId} />
      ))}</div> : null}
    </article>
  );
};

const GuestCard = ({ guest, onDisconnectConnection, busyKey }) => (
  <article className="systemMonitor__userCard is-guest">
    <div className="systemMonitor__userHead">
      <div className="systemMonitor__userIdentity">
        <div className="systemMonitor__avatar">G</div>
        <div><div className="systemMonitor__userNameRow"><strong>{guest.name || 'Гость'}</strong><span className="systemMonitor__roleBadge">guest</span></div><div className="systemMonitor__userSub">{guest.connections?.[0]?.route || 'страница не определена'}</div></div>
      </div>
      <div className="systemMonitor__userStats"><span><b>{guest.connectionCount || 0}</b> sockets</span><span>активность {formatAgo(guest.lastActivityAt)} назад</span></div>
    </div>
    <div className="systemMonitor__connections is-open">{(guest.connections || []).map((connection) => (
      <ConnectionRow key={connection.socketId} connection={connection} onDisconnectConnection={onDisconnectConnection} busy={busyKey === connection.socketId} />
    ))}</div>
  </article>
);

const SystemMonitorUsers = ({ users = [], guests = [], blockedUsers = [], source = 'all', currentUserId, onDisconnectUser, onDisconnectConnection, onBlock, onUnblock, onHistory, busyKey }) => {
  const [query, setQuery] = useState('');
  const [showGuests, setShowGuests] = useState(true);

  const sourceUsers = useMemo(() => users.map((item) => filterGroupBySource(item, source)).filter(Boolean), [source, users]);
  const sourceGuests = useMemo(() => guests.map((item) => filterGroupBySource(item, source)).filter(Boolean), [guests, source]);
  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sourceUsers;
    return sourceUsers.filter((user) => [user.name, user.email, user.role, user.userId, ...(user.routes || [])].join(' ').toLowerCase().includes(needle));
  }, [query, sourceUsers]);

  return (
    <section className="systemMonitor__section">
      <div className="systemMonitor__sectionHead">
        <div><div className="systemMonitor__sectionTitle">Пользователи online</div><div className="systemMonitor__sectionHint">FRONT и ADMIN фильтруются отдельно; IP, route, вкладки и устройства видны по соединениям.</div></div>
        <div className="systemMonitor__usersTools">
          <input className="systemMonitor__search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Имя, email, роль, route" />
          <button type="button" className="systemMonitor__miniBtn" onClick={() => setShowGuests((v) => !v)}>{showGuests ? 'Скрыть гостей' : `Гости (${sourceGuests.length})`}</button>
        </div>
      </div>
      <div className="systemMonitor__usersList">
        {filteredUsers.map((user) => <UserCard key={user.key || user.userId} user={user} currentUserId={currentUserId} onDisconnectUser={onDisconnectUser} onDisconnectConnection={onDisconnectConnection} onBlock={onBlock} onUnblock={onUnblock} onHistory={onHistory} busyKey={busyKey} />)}
        {!filteredUsers.length ? <div className="systemMonitor__empty">Авторизованных пользователей online нет.</div> : null}
        {showGuests && sourceGuests.length ? <><div className="systemMonitor__guestDivider">Гости · {sourceGuests.length}</div>{sourceGuests.map((guest) => <GuestCard key={guest.key} guest={guest} onDisconnectConnection={onDisconnectConnection} busyKey={busyKey} />)}</> : null}
        {blockedUsers.length ? <>
          <div className="systemMonitor__guestDivider">Заблокированные offline · {blockedUsers.length}</div>
          {blockedUsers.map((blockedUser) => (
            <article className="systemMonitor__userCard is-blocked" key={blockedUser._id}>
              <div className="systemMonitor__userHead">
                <div><strong>{blockedUser.name}</strong><div className="systemMonitor__userSub">{blockedUser.email} · {blockedUser.role}</div></div>
                <button type="button" className="systemMonitor__miniBtn is-success" disabled={busyKey === String(blockedUser._id)} onClick={() => onUnblock(blockedUser)}>Разблокировать</button>
              </div>
            </article>
          ))}
        </> : null}
      </div>
    </section>
  );
};

export default SystemMonitorUsers;
